import React, { forwardRef } from 'react';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { Circle, CircleCheck, CirclePause, CircleX, ListChecks } from 'lucide-react';
import { StartedColumnIcon } from 'src/components/icons/StartedColumnIcon';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  isClickable: boolean;
  showBorderOnHover: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isClickable, showBorderOnHover }) => ({
  chip: {
    '&:hover': {
      borderColor: showBorderOnHover ? theme.appColors.rule : undefined,
    },
    alignItems: 'center',
    border: `1px solid transparent`,
    borderRadius: 8,
    cursor: isClickable ? 'pointer' : undefined,
    display: 'flex',
    gap: theme.spacing(0.5),
    maxWidth: '100%',
    overflow: 'hidden',
    padding: theme.spacing(0.25, 0.5),
    transition: 'all 0.2s ease-in-out',
    width: 'fit-content',
  },
  chipSelected: {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.appColors.rule,
  },
  icon: {
    display: 'flex',
    flexShrink: 0,
  },
  label: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
  },
}));

interface StatusChipProps {
  category: Apollo.ColumnCategory;
  color: string;
  label: string;
  clickable?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  selected?: boolean;
  showBorderOnHover?: boolean;
}

type CategoryIcon = React.ComponentType<{ color?: string; size?: number }>;

function StartedColumnIconAdapter({ color, size }: { color?: string; size?: number }) {
  return <StartedColumnIcon fill={color} height={size} width={size} />;
}

const CATEGORY_ICONS: Record<Apollo.ColumnCategory, CategoryIcon> = {
  Cancelled: CircleX,
  Done: CircleCheck,
  NotStarted: Circle,
  Paused: CirclePause,
  Started: StartedColumnIconAdapter,
  Triage: ListChecks,
};

export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(function StatusChip(
  {
    category,
    clickable = false,
    color,
    label,
    onClick,
    selected = false,
    showBorderOnHover = true,
  },
  ref
) {
  const { classes } = useStyles({ isClickable: clickable, showBorderOnHover });
  const Icon = CATEGORY_ICONS[category];

  return (
    <div
      ref={ref}
      className={clsx(classes.chip, selected && classes.chipSelected)}
      onClick={onClick}
    >
      <div className={classes.icon}>
        <Icon color={color} size={16} />
      </div>
      <Typography noWrap variant="body2">
        {label}
      </Typography>
    </div>
  );
});
